import type { Middleware } from "@reduxjs/toolkit";
import { isAppointmentTask } from "../constants/AppointmentOverlayTasks";
import { isVisitActivity } from "../constants/AppointmentActivity";
import type { AppointmentOverlayState } from "./slices/appointmentOverlaySlice";

export const APPOINTMENT_OVERLAY_STORAGE_KEY = "spire360.appointmentOverlay.v1";

/** State older than this is treated as abandoned and dropped on load. */
export const OVERLAY_STATE_SYNCED = "appointmentOverlay/overlayStateSynced";

export const APPOINTMENT_OVERLAY_MAX_AGE_MS = 12 * 60 * 60 * 1000;

const isPersistedOverlayState = (
  value: unknown
): value is AppointmentOverlayState => {
  if (typeof value !== "object" || value === null) return false;
  const state = value as Partial<AppointmentOverlayState>;
  return (
    typeof state.appointmentId === "string" &&
    typeof state.patientId === "string" &&
    typeof state.patientName === "string" &&
    typeof state.startedAt === "string" &&
    typeof state.detailsPath === "string" &&
    typeof state.collapsed === "boolean" &&
    Array.isArray(state.tasks) &&
    state.tasks.every(isAppointmentTask) &&
    // `connectivity` and `scheduledMinutes` were added after the first cut of this
    // key; tolerate their absence so an appointment open across a deploy is not
    // dropped.
    (state.scheduledMinutes === undefined ||
      state.scheduledMinutes === null ||
      typeof state.scheduledMinutes === "number") &&
    (state.connectivity === undefined ||
      state.connectivity === null ||
      typeof state.connectivity === "object") &&
    // Added later still (the chosen focus); absent in older saved state.
    (state.journeyStepId === undefined ||
      state.journeyStepId === null ||
      typeof state.journeyStepId === "string") &&
    (state.checklistRowCount === undefined ||
      state.checklistRowCount === null ||
      typeof state.checklistRowCount === "number") &&
    (state.patientVerified === undefined ||
      typeof state.patientVerified === "boolean") &&
    // Added with the activity log (WCP-13264); reloaded from the API anyway.
    (state.visitActivities === undefined ||
      (Array.isArray(state.visitActivities) &&
        state.visitActivities.every(isVisitActivity)))
  );
};

// Shared by every tab, so the overlay follows the clinician into new tabs.
const storage = () => window.localStorage;

const parseOverlayState = (raw: string | null): AppointmentOverlayState | undefined => {
  if (!raw) return undefined;
  const parsed: unknown = JSON.parse(raw);
  if (!isPersistedOverlayState(parsed)) return undefined;

  const startedAt = Date.parse(parsed.startedAt as string);
  if (Number.isNaN(startedAt)) return undefined;
  if (Date.now() - startedAt > APPOINTMENT_OVERLAY_MAX_AGE_MS) {
    clearOverlayState();
    return undefined;
  }

  return {
    ...parsed,
    connectivity: parsed.connectivity ?? null,
    scheduledMinutes: parsed.scheduledMinutes ?? null,
    journeyStepId: parsed.journeyStepId ?? null,
    checklistRowCount: parsed.checklistRowCount ?? null,
    patientVerified: parsed.patientVerified ?? false,
    visitActivities: parsed.visitActivities ?? [],
  };
};

// Moves an appointment saved before the switch to localStorage over, once.
const migrateFromSessionStorage = (): string | null => {
  const raw = window.sessionStorage.getItem(APPOINTMENT_OVERLAY_STORAGE_KEY);
  if (raw) {
    window.sessionStorage.removeItem(APPOINTMENT_OVERLAY_STORAGE_KEY);
    storage().setItem(APPOINTMENT_OVERLAY_STORAGE_KEY, raw);
  }
  return raw;
};

export const loadOverlayState = (): AppointmentOverlayState | undefined => {
  try {
    const raw =
      storage().getItem(APPOINTMENT_OVERLAY_STORAGE_KEY) ?? migrateFromSessionStorage();
    return parseOverlayState(raw);
  } catch {
    return undefined;
  }
};

export const saveOverlayState = (state: AppointmentOverlayState): void => {
  try {
    storage().setItem(APPOINTMENT_OVERLAY_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Storage unavailable or over quota -- the overlay still works for this page view.
  }
};

export const clearOverlayState = (): void => {
  try {
    storage().removeItem(APPOINTMENT_OVERLAY_STORAGE_KEY);
  } catch {
    // Nothing to do; see saveOverlayState.
  }
};

/**
 * Persists the slice whenever one of its actions is dispatched. Keeping this in
 * middleware rather than a component effect means persistence is off the render path
 * and does not depend on the overlay being mounted.
 */
export const appointmentOverlayPersistence: Middleware = (api) => (next) => (
  action
) => {
  const result = next(action);

  const type = (action as { type?: unknown }).type;
  if (
    typeof type === "string" &&
    type.startsWith("appointmentOverlay/") &&
    // Already what's stored: it came from another tab's write.
    type !== OVERLAY_STATE_SYNCED
  ) {
    const { appointmentOverlay } = api.getState() as {
      appointmentOverlay: AppointmentOverlayState;
    };
    if (appointmentOverlay.appointmentId) {
      saveOverlayState(appointmentOverlay);
    } else {
      clearOverlayState();
    }
  }

  return result;
};

// Hands this tab another tab's start, change, end or logout (undefined: no appointment).
export const subscribeToOverlayChanges = (
  onChange: (state: AppointmentOverlayState | undefined) => void
): (() => void) => {
  const listener = (event: StorageEvent) => {
    if (event.key !== APPOINTMENT_OVERLAY_STORAGE_KEY && event.key !== null) return;
    if (event.storageArea !== storage()) return;
    try {
      onChange(parseOverlayState(event.newValue));
    } catch {
      onChange(undefined);
    }
  };
  window.addEventListener("storage", listener);
  return () => window.removeEventListener("storage", listener);
};
